import { useEffect, useId, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import { PencilIcon, Trash2Icon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { useApi } from '@/lib/legacy';
import { notifyError, notifySuccess } from '@/lib/notify';
import { quartzTheme } from '../../../ui/components/Grid/themes';
import { uniqueId } from '../../../ui/utils';
import { usePolicyPageContext as usePolicyPageContextJs } from '../context/PolicyPageContext';
import MultiSelect, { type LocationOption } from './MultiSelect';
import type { Row } from '../tabParts';

type PolicyPageState = { MCEsData: Row[]; policy: Row; groups?: Row[]; fetchAndSetGroups: () => void; isFetchingGroups: boolean };
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: pages/PolicyPage/context/PolicyPageContext.jsx
const usePolicyPageContext = usePolicyPageContextJs as any as () => PolicyPageState;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: AG Grid cell renderer params
type CellParams = any;
type Group = { id: string; name: string; parentId: string; distribute?: string; selectedMonth?: string; children: Row[] };
type SetGroups = (update: (prev: Group[]) => Group[]) => void;

const NO_PARENT = '__none__';

function LocationDropdown({
  selectedValue,
  setSelectedValue,
  options,
}: {
  selectedValue: string;
  setSelectedValue: (value: string) => void;
  options: Row[];
}) {
  const filteredOptions = options.filter((loc) => loc.id !== selectedValue);
  const selected = options.find((loc) => loc.id === selectedValue);
  const items = [
    ...(selectedValue ? [{ value: selectedValue, label: `${selected?.riskId}. ${selected?.name || 'Selected Location'}`, disabled: true }] : []),
    { value: NO_PARENT, label: 'Select Location', disabled: false },
    ...filteredOptions.map((loc) => ({ value: loc.id as string, label: `${loc?.riskId}. ${loc.name}`, disabled: false })),
  ];
  return (
    <Select items={items} value={selectedValue || NO_PARENT} onValueChange={(value) => setSelectedValue(value === NO_PARENT ? '' : String(value))}>
      <SelectTrigger aria-label="Parent location" className="w-full bg-white">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {items.map((item) => (
          <SelectItem key={item.value} value={item.value} disabled={item.disabled}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function DistributeChoice({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  const id = useId();
  return (
    <div className="flex flex-row items-center gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={onChange} />
      <Label htmlFor={id} className="text-sm font-normal">
        {label}
      </Label>
    </div>
  );
}

function ActionButtons({ params, setGroupsData }: { params: CellParams; setGroupsData: SetGroups }) {
  const [selectedOption, setSelectedOption] = useState('');

  useEffect(() => {
    setSelectedOption(params.data.distribute || '');
  }, [params.data]);

  const handleCheckboxChange = (option: string) => {
    const newOption = selectedOption === option ? '' : option;
    setSelectedOption(newOption);
    params.api.applyTransaction({ update: [{ ...params.data, distribute: newOption }] });
    setGroupsData((prevGroups) =>
      prevGroups.map((group) => (group.id === params.data.id ? { ...group, distribute: group.distribute === option ? '' : option } : group))
    );
  };

  return (
    <div className="mt-2 flex flex-row items-center gap-2 p-1.5">
      <div className="flex flex-1 flex-col items-start gap-2">
        {[
          { key: 'inherit', label: 'Inherit TIV' },
          { key: 'proportional', label: 'Proportional TIV' },
        ].map((option) => (
          <DistributeChoice
            key={option.key}
            label={option.label}
            checked={selectedOption === option.key}
            onChange={() => handleCheckboxChange(option.key)}
          />
        ))}
      </div>
      <div className="flex flex-row items-center gap-1.5">
        <Button
          variant="outline"
          size="icon-sm"
          aria-label="Rename group"
          onClick={() => params.api.startEditingCell({ rowIndex: params.rowIndex, colKey: 'name' })}
        >
          <PencilIcon />
        </Button>
        <Button
          variant="outline"
          size="icon-sm"
          aria-label="Remove group"
          onClick={() => {
            params.api.applyTransaction({ remove: [params.data] });
            setGroupsData((prev) => prev.filter((group) => group.id !== params.data.id));
          }}
        >
          <Trash2Icon />
        </Button>
      </div>
    </div>
  );
}

function ChildGrid({ params, locations, setGroupsData }: { params: CellParams; locations: Row[]; setGroupsData: SetGroups }) {
  const { data } = params;

  const handleDelete = (childParams: CellParams) => {
    childParams.api.applyTransaction({ remove: [childParams.data] });
    setGroupsData((prevGroups) =>
      prevGroups.map((group) =>
        group.id === data.id ? { ...group, children: group.children.filter((child) => child.locationId !== childParams.data.locationId) } : group
      )
    );
  };

  const childColumnDefs: ColDef[] = [
    {
      field: 'name',
      headerName: 'Location',
      rowDrag: true,
      valueGetter: (p: CellParams) => p?.data?.name || locations.find((location) => location.id === p.data.locationId)?.name,
    },
    {
      field: 'amount',
      headerName: 'Flat Amount ($)',
      editable: !params.data.distribute,
      valueFormatter: (p: CellParams) => (p?.data?.amount ? `$${Number(p?.data?.amount)?.toFixed(2)}` : ''),
      cellClassRules: { 'ag-cell-disable': () => params.data.distribute, 'ag-cell-editable': () => !params.data.distribute },
    },
    {
      field: 'percentage',
      headerName: 'Percentage (%)',
      editable: !params.data.distribute,
      cellClassRules: { 'ag-cell-disable': () => params.data.distribute, 'ag-cell-editable': () => !params.data.distribute },
      valueFormatter: (p: CellParams) => p.data.percentage && `${p.data.percentage}%`,
    },
    {
      headerName: 'Actions',
      cellRenderer: (p: CellParams) => (
        <Button variant="outline" size="icon-xs" aria-label="Remove location" onClick={() => handleDelete(p)}>
          <Trash2Icon />
        </Button>
      ),
    },
  ];

  const handleChildGridChange = (p: CellParams) => {
    const { data: row, colDef, newValue } = p;
    if (colDef.field === 'amount' && newValue) {
      row.percentage = null;
    } else if (colDef.field === 'percentage' && newValue) {
      row.amount = null;
    }
  };

  return (
    <div className="h-full max-h-[280px] overflow-auto rounded-md border bg-muted/40 p-2.5">
      <AgGridReact
        theme={quartzTheme}
        columnMenu="legacy"
        rowData={data.children}
        columnDefs={childColumnDefs}
        defaultColDef={{ flex: 1, minWidth: 100 }}
        domLayout="autoHeight"
        rowDragManaged={true}
        onCellValueChanged={handleChildGridChange}
      />
    </div>
  );
}

/** Groups of locations combined for monthly reporting: each with a parent, children and a split. */
const CombineLocationModal = ({
  showCombineLocationsModal,
  handleClose = () => {},
}: {
  showCombineLocationsModal: boolean;
  handleClose?: () => void;
}) => {
  const { MCEsData, policy, groups = [], fetchAndSetGroups, isFetchingGroups } = usePolicyPageContext();
  const { updateGroups } = useApi();
  const locationChangeMCEs = MCEsData.filter((mce) => mce.type === 'locationChange' && (mce.status === 'executed' || mce.status === 'released'));
  locationChangeMCEs.sort((a, b) => Number(new Date(b.effectiveAt)) - Number(new Date(a.effectiveAt)));
  const mostRecentExecutedMCE = locationChangeMCEs[0];
  const locations: Row[] = mostRecentExecutedMCE?.locations?.length > 0 ? mostRecentExecutedMCE?.locations : policy?.locations;
  const [groupsData, setGroupsData] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const gridRef = useRef(null);

  useEffect(() => {
    setGroupsData(groups.map((group) => ({ ...(group as Group), id: uniqueId() })));
  }, [groups]);

  const generatePayload = (data: Group[]) =>
    data.map((group) => ({
      name: group.name,
      parentId: group.parentId,
      distribute: group.distribute || '',
      children: group.children.map((child) => ({
        locationId: child.locationId,
        amount: Number(child.amount),
        percentage: Number(child.percentage),
        name: child.name,
      })),
    }));

  const validatePayload = (payload: { parentId: string; name: string }[]) => {
    const missingParentIds = payload.filter((group) => !group.parentId).map((group) => group.name || 'Unnamed Group');
    return missingParentIds.length > 0 ? `Please Select Parent Location for the following Groups: ${missingParentIds.join(', ')}.` : '';
  };

  const handleSaveData = async () => {
    const payload = generatePayload(groupsData);
    if (!groups.length && !payload.length) {
      setErrorMessage('Please add a group before saving.');
      return;
    }
    const error = validatePayload(payload);
    if (error) {
      setErrorMessage(error);
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    setLoading(true);
    setErrorMessage('');
    try {
      const response = await updateGroups(policy.id, payload);
      if (response) {
        fetchAndSetGroups();
        notifySuccess('Updated groups successfully');
      }
    } catch (err) {
      notifyError('Unable to create or update the groups.');
      setErrorMessage('Unable to create or update the groups.');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  const addGroup = () => {
    setGroupsData([...groupsData, { id: uniqueId(), name: `New Group ${groupsData.length + 1}`, parentId: '', selectedMonth: '', children: [] }]);
  };

  const handleSelectedLocationsChange = (groupId: string, newSelectedLocations: LocationOption[]) => {
    setGroupsData((prevData) =>
      prevData.map((group) =>
        group.id === groupId
          ? {
              ...group,
              children: newSelectedLocations.map((loc) => ({
                locationId: loc?.id || loc?.locationId,
                amount: loc?.amount || null,
                percentage: loc?.percentage || null,
                name: loc?.name,
              })),
            }
          : group
      )
    );
  };

  const getAvailableLocations = (excludeLocationIds: unknown[] = []) =>
    locations.filter((loc) => !excludeLocationIds.includes(loc.id || loc?.locationId));
  const getSelectedLocations = (params: CellParams) =>
    groupsData.filter(({ parentId }) => parentId !== params?.data?.parentId).map(({ parentId }) => parentId);
  const getSelectedChildLocations = (params: CellParams) =>
    groupsData
      .flatMap(({ children }) => children)
      .filter(({ locationId }) => locationId !== params?.data?.locationId)
      .map(({ locationId }) => locationId);

  const columnDefs: ColDef[] = [
    { field: 'name', headerName: 'Group Name', editable: true, cellRenderer: 'agGroupCellRenderer', minWidth: 200 },
    {
      field: 'parentId',
      headerName: 'Parent Location',
      cellRenderer: (params: CellParams) => {
        const availableParentLocations = getAvailableLocations([...getSelectedLocations(params), ...getSelectedChildLocations(params)]);
        return (
          <LocationDropdown
            selectedValue={params.value}
            setSelectedValue={(newValue) => {
              setGroupsData((prev) =>
                prev.map((group) =>
                  group.id === params.data.id
                    ? { ...group, parentId: newValue, children: group?.children?.filter((child) => child.locationId !== newValue) }
                    : group
                )
              );
              params.api.refreshCells({ rowNodes: [params.node], columns: ['parentId'] });
            }}
            options={availableParentLocations}
          />
        );
      },
      editable: false,
      flex: 1,
      minWidth: 200,
    },
    {
      headerName: 'Select Child Locations',
      field: 'locations',
      width: 600,
      cellRenderer: (params: CellParams) => {
        const availableChildLocations = getAvailableLocations([
          ...getSelectedChildLocations(params),
          ...getSelectedLocations(params),
          params.data.parentId,
        ]);
        return (
          <MultiSelect
            selectedValues={
              locations.filter((loc) => (params.data.children || []).some((child: Row) => child.locationId === loc.id)) as LocationOption[]
            }
            setSelectedValues={(newValues) => handleSelectedLocationsChange(params.data.id, newValues)}
            options={availableChildLocations as LocationOption[]}
          />
        );
      },
      cellStyle: { overflowY: 'auto', maxHeight: 800 },
      minWidth: 200,
    },
    {
      headerName: 'Actions',
      flex: 1,
      cellRenderer: (params: CellParams) => <ActionButtons params={params} setGroupsData={setGroupsData} />,
      minWidth: 300,
    },
  ];

  const gridOptions = {
    masterDetail: true,
    detailCellRenderer: (params: CellParams) => <ChildGrid params={params} locations={locations} setGroupsData={setGroupsData} />,
    getRowHeight: (params: CellParams) => {
      if (params.node.detail) {
        return 400;
      }
      const selectedLocations = params.data?.children || [];
      return Math.max(80, selectedLocations.length * 25);
    },
  };

  return (
    <Dialog
      open={showCombineLocationsModal}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent className="max-w-[90%] sm:max-w-[90%]">
        <DialogHeader>
          <DialogTitle className="capitalize">Combine Locations</DialogTitle>
        </DialogHeader>
        <div className="relative flex h-[500px] flex-col overflow-hidden rounded-lg bg-white p-6 shadow-md">
          {isFetchingGroups ? (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
              <Spinner className="mx-auto size-8" />
              <p className="mt-3 text-base font-bold">Fetching Groups...</p>
            </div>
          ) : (
            <>
              <Button className="mb-3 w-[120px] self-end rounded-lg" onClick={() => addGroup()}>
                + Add Group
              </Button>
              <div className="grid-container w-full flex-1 overflow-y-auto rounded-lg border p-2 shadow-sm">
                <AgGridReact
                  theme={quartzTheme}
                  columnMenu="legacy"
                  masterDetail={true}
                  ref={gridRef}
                  rowData={groupsData as Row[]}
                  gridOptions={gridOptions}
                  columnDefs={columnDefs}
                  context={{ locations }}
                  domLayout="autoHeight"
                  defaultColDef={{ flex: 1 }}
                />
              </div>
              {errorMessage ? (
                <div className="mt-2.5 rounded-md border border-destructive bg-destructive/10 p-1.5 text-xs text-destructive">{errorMessage}</div>
              ) : null}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" className="text-primary" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={() => handleSaveData()} disabled={loading}>
            {loading ? <Spinner /> : null}
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CombineLocationModal;
